import { useEffect } from "react";
import { Link } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import { AMOK_HOME_ARTICLE as ARTICLE } from "@/lib/amokAtHomeArticle";

export default function AmokAtHomeArticlePage() {
  const canonical = `https://www.angkorflavours.com.au${ARTICLE.path}`;
  const image = `https://www.angkorflavours.com.au${ARTICLE.image}`;
  useEffect(() => {
    let tag = document.head.querySelector<HTMLMetaElement>(
      'meta[property="og:type"]'
    );
    const created = !tag;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", "og:type");
      document.head.appendChild(tag);
    }
    tag.content = "article";
    return () => {
      if (created) tag?.remove();
      else if (tag) tag.content = "website";
    };
  }, []);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonical}#article`,
    headline: ARTICLE.headline,
    datePublished: ARTICLE.datePublished,
    dateModified: ARTICLE.datePublished,
    image,
    mainEntityOfPage: canonical,
    author: { "@type": "Organization", name: "ANGKOR team" },
    publisher: { "@type": "Organization", name: "ANGKOR Cambodian Flavours" },
  };
  return (
    <article className="blog-article">
      <PageMeta
        title={ARTICLE.title}
        description={ARTICLE.description}
        path={ARTICLE.path}
        image={image}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <section className="intent-hero">
        <div className="container intent-hero-grid">
          <div>
            <Link href="/blog" className="eyebrow blog-back-link">
              Blog
            </Link>
            <h1>{ARTICLE.headline}</h1>
            <p className="blog-byline">
              <time dateTime={ARTICLE.datePublished}>{ARTICLE.dateLabel}</time>{" "}
              · {ARTICLE.byline}
            </p>
          </div>
          <img
            src={ARTICLE.image}
            sizes="(max-width: 767px) calc(100vw - 32px), 50vw"
            alt={ARTICLE.imageAlt}
            width="640"
            height="800"
            style={{ width: "100%", height: "auto", objectFit: "contain" }}
            fetchPriority="high"
            loading="eager"
          />
        </div>
      </section>
      <section className="section parchment-section">
        <div className="container">
          <div className="blog-article-body">
            <p>
              {
                "Fish Amok is the dish most people think of when they think of Cambodian food: a softly set coconut curry, fragrant with lemongrass, galangal and kaffir lime, traditionally steamed in a banana-leaf cup. It is the signature dish at our restaurant in West Melbourne, and it is also one of the most rewarding Khmer dishes to make at home. The secret is not complicated technique. It is the paste."
              }
            </p>
            <p>
              {"This guide shows you a simple home method using our "}
              <Link href="/products/angkor-amok-curry-paste-280g?utm_source=blog&utm_medium=organic&utm_campaign=amok-at-home">
                {"Amok Curry Paste"}
              </Link>
              {
                ", the same style of fragrant kroeung we make from scratch in our kitchen."
              }
            </p>
            <h2>{"What is kroeung, and why does it matter?"}</h2>
            <p>
              {
                'Kroeung (pronounced roughly "kreung") is the aromatic paste at the heart of Cambodian cooking. Families pound fresh roots, herbs and spices together, and the exact balance changes from kitchen to kitchen. For amok, the kroeung is rich and golden from turmeric, bright from lemongrass and kaffir lime, and gently warm rather than fiery.'
              }
            </p>
            <p>
              {
                "Making kroeung by hand takes time and a good mortar and pestle. That is why we put ours in a jar. Each 280 g jar of ANGKOR Amok Curry Paste contains galangal, shallots, garlic, turmeric, lemongrass, shrimp, kaffir lime, fish sauce, vegetable oil and long red chilli. Open the lid and you get the same aroma that fills our kitchen at 389 Spencer Street."
              }
            </p>
            <h2>{"What you will need"}</h2>
            <p>
              {
                "This is a flexible home method, not a strict recipe, so adjust it to your taste and your pan. For about two to three people:"
              }
            </p>
            <ul>
              <li>
                {
                  "2 to 3 heaped tablespoons of ANGKOR Amok Curry Paste (start with less and add more if you like it stronger)"
                }
              </li>
              <li>
                {
                  "400 ml coconut milk (keep a few spoonfuls of the thick cream from the top for finishing)"
                }
              </li>
              <li>
                {
                  "About 400 to 500 g firm white fish fillets, cut into bite-sized pieces"
                }
              </li>
              <li>
                {
                  "1 egg (optional, it helps the curry set into the soft, custard-like texture amok is known for)"
                }
              </li>
              <li>
                {"A handful of greens, such as baby spinach or Asian greens"}
              </li>
              <li>{"Fish sauce and a pinch of sugar, to taste"}</li>
              <li>
                {
                  "Fresh kaffir lime leaves and long red chilli, finely sliced, to garnish"
                }
              </li>
              <li>{"Steamed jasmine rice to serve"}</li>
              <li>
                {"Banana leaves, ramekins or a heatproof bowl for steaming"}
              </li>
            </ul>
            <img
              src={ARTICLE.secondImage}
              alt={ARTICLE.secondImageAlt}
              width="640"
              height="800"
              style={{
                display: "block",
                width: "100%",
                maxWidth: "640px",
                height: "auto",
                objectFit: "contain",
                margin: "2rem auto",
              }}
              loading="lazy"
              decoding="async"
            />
            <h2>{"Step by step"}</h2>
            <p>
              <strong>{"1. Bloom the paste."}</strong>
              {
                " Warm a spoonful of the thick coconut cream in a pan over medium heat, then add the curry paste. Stir for a minute or two until it smells fragrant. This wakes up the aromatics."
              }
            </p>
            <p>
              <strong>{"2. Build the sauce."}</strong>
              {
                " Pour in most of the coconut milk and stir until smooth. Taste it now. Season with a splash of fish sauce and a small pinch of sugar if you want more balance. Let it cool slightly."
              }
            </p>
            <p>
              <strong>{"3. Add the fish."}</strong>
              {
                " Fold the fish pieces through the sauce so every piece is well coated. If you are using an egg, whisk it in once the sauce is no longer hot, so it does not scramble."
              }
            </p>
            <p>
              <strong>{"4. Fill your cups."}</strong>
              {
                " Line ramekins or a heatproof bowl with greens (or fold banana leaves into small cups, which is the traditional way). Spoon in the fish and sauce."
              }
            </p>
            <p>
              <strong>{"5. Steam gently."}</strong>
              {
                " Steam over simmering water until the fish is just cooked through and the curry has softly set, usually around 15 to 20 minutes depending on the size of your cups. Keep the heat gentle, as amok should be silky, not rubbery."
              }
            </p>
            <p>
              <strong>{"6. Finish and serve."}</strong>
              {
                " Top with a little reserved coconut cream, finely sliced kaffir lime leaf and a few slivers of red chilli. Serve straight away with steamed jasmine rice."
              }
            </p>
            <p>
              <strong>{"No steamer?"}</strong>
              {
                ' You can make a quicker "saucy" amok in the pan. After step 2, add the fish directly to the simmering sauce, cover and cook gently for a few minutes until the fish is opaque, then stir through the greens. It is less traditional, but it is a delicious weeknight dinner.'
              }
            </p>
            <h2>{"Tips and swaps"}</h2>
            <ul>
              <li>
                <strong>{"Choose a firm fish."}</strong>
                {" Firm white fillets hold their shape best while steaming."}
              </li>
              <li>
                <strong>{"Try other proteins."}</strong>
                {
                  " Chicken thigh, prawns or firm tofu with the coconut sauce all work well with amok paste. Chicken needs a little longer to cook through."
                }
              </li>
              <li>
                <strong>{"Go easy on the salt."}</strong>
                {
                  " The paste already contains fish sauce, so taste before you add more."
                }
              </li>
              <li>
                <strong>{"Leftover paste."}</strong>
                {
                  " Keep the jar closed and refrigerated after opening, and always use a clean spoon. A spoonful also lifts a stir-fry, a coconut soup or a marinade for grilled chicken."
                }
              </li>
            </ul>
            <h2>{"Allergen information"}</h2>
            <p>
              {
                "Please read this before you order or cook. ANGKOR Amok Curry Paste "
              }
              <strong>{"contains crustacean (shrimp) and fish"}</strong>
              {
                ". Our kitchen also handles peanuts, tree nuts, gluten, soy, sesame, egg, milk, crustaceans and fish. If you are cooking for guests, check their allergies first."
              }
            </p>
            <h2>{"Order the paste"}</h2>
            <p>
              {"A 280 g jar is "}
              <strong>{"$15.00 including GST"}</strong>
              {". We ship Australia-wide by Parcel Post for a flat "}
              <strong>{"$22.75 per order"}</strong>
              {
                " (Australia only), so it is worth grabbing a few jars at once for yourself and for friends who love Southeast Asian cooking."
              }
            </p>
            <p>
              <Link href="/products/angkor-amok-curry-paste-280g?utm_source=blog&utm_medium=organic&utm_campaign=amok-at-home">
                {"Shop ANGKOR Amok Curry Paste"}
              </Link>
            </p>
            <h2>{"Or let us cook it for you"}</h2>
            <p>
              {
                "There is nothing quite like Fish Amok made by the family that has been cooking it for years. Read more about the dish on our "
              }
              <Link href="/fish-amok-melbourne">
                {"Fish Amok in Melbourne"}
              </Link>
              {" page, browse the "}
              <Link href="/menu">{"dinner menu"}</Link>
              {", or explore more Khmer dishes in our "}
              <Link href="/blog/khmer-family-recipes-melbourne">
                {"Khmer family recipes"}
              </Link>
              {" article."}
            </p>
            <p>
              {
                "ANGKOR Cambodian Flavours is at 389 Spencer Street, West Melbourne, a short walk from Southern Cross Station and Marvel Stadium. Our hours are:"
              }
            </p>
            <ul>
              <li>{"Monday: closed"}</li>
              <li>
                {"Tuesday to Thursday: 11:30am to 3pm and 4:30pm to 9:30pm"}
              </li>
              <li>{"Friday: 11:30am to 9:30pm"}</li>
              <li>{"Saturday: 12pm to 9:30pm"}</li>
              <li>{"Sunday: 4:30pm to 9:30pm"}</li>
            </ul>
            <p>
              <a href="https://reserve.oddle.me/en_AU/angkorcafe">
                {"Book a table"}
              </a>
              {" or call us on (03) 7034 8353."}
            </p>
          </div>
        </div>
      </section>
    </article>
  );
}
